// Create / edit a custom role. Controlled MUI-style fields (plain useState, as in
// solya-app's settings dialogs). On create: name + displayName + description. On
// edit: displayName + description only (name/isSystem/org are immutable here).

import { useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { RoleDetailDto, RoleDto } from "../api/types.js"
import { fr } from "../i18n/fr.js"
import { isDuplicateError, messageForError } from "../lib/errors.js"
import { Drawer } from "../ui/Drawer.js"
import { Button, Field } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

interface Props {
  mode: "create" | "edit"
  org: string
  role?: RoleDto
  onClose: () => void
  onSaved: (role: RoleDetailDto) => void
}

const NAME_MIN = 2
const NAME_MAX = 255

export function RoleFormDrawer({ mode, org, role, onClose, onSaved }: Props) {
  const notify = useNotify()
  const [name, setName] = useState(role?.name ?? "")
  const [displayName, setDisplayName] = useState(role?.displayName ?? "")
  const [description, setDescription] = useState(role?.description ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const nameValid = mode === "edit" || (name.trim().length >= NAME_MIN && name.length <= NAME_MAX)
  const canSubmit = nameValid && !submitting

  async function submit() {
    setSubmitting(true)
    setFormError(null)
    try {
      const saved =
        mode === "create"
          ? await adminApi.createRole({
              name: name.trim(),
              displayName: displayName.trim() || undefined,
              description: description.trim() || null,
              organizationId: org
            })
          : await adminApi.updateRole((role as RoleDto).id, {
              displayName: displayName.trim(),
              description: description.trim() || null
            })
      notify.success(fr.app.save)
      onSaved(saved)
      onClose()
    } catch (err) {
      if (mode === "create" && isDuplicateError(err)) {
        setFormError(fr.roles.create.errorDuplicate)
      } else {
        setFormError(messageForError(err, fr.roles.create.errorGeneric))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const title = mode === "create" ? fr.roles.create.title : fr.roles.edit.title
  const submitLabel = mode === "create" ? fr.roles.create.submit : fr.roles.edit.submit

  return (
    <Drawer
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {fr.app.cancel}
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </>
      }
    >
      {mode === "create" ? (
        <Field label={fr.roles.create.nameLabel} htmlFor="role-name">
          <input
            id="role-name"
            className="field__input mono"
            value={name}
            maxLength={NAME_MAX}
            placeholder={fr.roles.create.namePlaceholder}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      ) : null}

      <Field label={fr.roles.create.displayNameLabel} htmlFor="role-display">
        <input
          id="role-display"
          className="field__input"
          value={displayName}
          maxLength={NAME_MAX}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </Field>

      <Field label={fr.roles.create.descriptionLabel} htmlFor="role-desc">
        <textarea
          id="role-desc"
          className="field__textarea"
          value={description}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      {formError ? <div className="field__error">{formError}</div> : null}
    </Drawer>
  )
}
